import React, { useEffect } from "react";
import { Box, Grid } from "@mui/material";
import CustomTable from "../components/Common/CustomTable";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import ParserConfigFilter from "../components/ParserConfig/ParserConfigFilter";
import ParserConfigMenu from "../components/ParserConfig/ParserConfigMenu";
import ParserConfigUpdate from "../components/ParserConfig/ParserConfigUpdate";
import { useParserConfigData } from "./hooks/useParserConfigData";
import { useParserConfigMenu } from "./hooks/useParserConfigMenu";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import { MoreVert } from "@mui/icons-material";
import { ParserConfigDto } from "../types";

type ParserConfigProps = {
  deviceTypeId: number;
};

const ParserConfig = ({ deviceTypeId }: ParserConfigProps) => {
  const { showError, snackbarProps } = useSnackbar();

  const {
    data,
    totalCount,
    page,
    rowPerPage,
    loading,
    searchText,
    setPage,
    getDataWithPagination,
    fetchConnectionType,
    debouncedSearch,
    handleChangePage,
    handleRowsPerPageChange,
    handleSearchChange,
  } = useParserConfigData(deviceTypeId);

  const {
    anchorEl,
    selectedRow,
    isUpdateOpen,
    handleMenuClick,
    handleCloseMenu,
    handleView,
    handleEdit,
    handleCloseUpdate,
  } = useParserConfigMenu(deviceTypeId);


  const columns = [
    { id: "parserName", label: "Parser Adı" },
    { id: "description", label: "Açıklama" },
    { id: "connectionTypeName", label: "Bağlantı Tipi" },
    { id: "payloadTypeName", label: "Veri Tipi" },
    {
      id: "icon",
      label: "",
      render: (row: ParserConfigDto) => (
        <MoreVert
          style={{ cursor: "pointer" }}
          onClick={(event: React.MouseEvent<SVGSVGElement>) =>
            handleMenuClick(event, row)
          }
        />
      ),
    },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    debouncedSearch(page, rowPerPage, searchText);
    return () => debouncedSearch.cancel();
  }, [page, rowPerPage, searchText, debouncedSearch]);

  const loadInitialData = async () => {
    try {
      await fetchConnectionType();
    } catch (error) {
      showError(error as Error);
    }
  };

  const loadParserConfigs = async () => {
    try {
      await getDataWithPagination(page, rowPerPage, searchText);
    } catch (error) {
      showError(error as Error);
    }
  };

  return (
    <>
      <div className="panel-container">
        <Grid
          container
          direction={"column"}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid size={12}>
            <Box
              sx={{
                backgroundColor: "#ffffff",
                padding: "15px",
                alignItems: "center",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: "20px",
              }}
            >
              <ParserConfigFilter
                searchText={searchText}
                onSearchChange={handleSearchChange}
                setPage={setPage}
                loadParserConfigs={loadParserConfigs}
                deviceTypeId={deviceTypeId}
              />
            </Box>
          </Grid>
          <Grid
            size={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomTable
              columns={columns}
              data={[...data]}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              onRowClick={() => {}}
              collapsible={false}
              selectable={false}
              sortable={true}
              statusFunction={() => {}}
              showActions={false}
              loading={loading}
            />
          </Grid>
        </Grid>

        <ParserConfigMenu
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          onView={handleView}
          onEdit={handleEdit}
        />

        <ParserConfigUpdate
          open={isUpdateOpen}
          onClose={handleCloseUpdate}
          onSuccess={loadParserConfigs}
          parserConfig={selectedRow}
          deviceTypeId={deviceTypeId}
        />
      </div>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default ParserConfig;
